'use client';

import { useState } from 'react';

import { Box, Button, Collapse, Stack, Typography } from '@mui/material';
import cx from 'clsx';
import { useTranslations } from 'next-intl';

import Save from '@/components/SvgIcons/Save';
import Settings from '@/components/SvgIcons/Settings';
import { Link as LinkTranslation } from '@/i18n/navigation';
import { setCookieConsent } from '@/lib/cookie-consent';
import colors from '@/styles/themes/colors';
import { CookieSettings, CookieSettingsConfigArray } from '@/types/cookie.type';
import useToggleState from '@/utils/hooks/useToggleState';

import CookieAccordion from './CookieAccordion';
import styles from './CookieConsent.module.scss';
import CookieList from './CookieList';

interface CookieConsentProps {
  showConsent: boolean;
}

const renderCookiePolicyLink = (chunks: React.ReactNode) => (
  <LinkTranslation href="/cookie-policy" target="_blank" className={styles.link}>
    {chunks}
  </LinkTranslation>
);

const CookieConsent = ({ showConsent }: CookieConsentProps) => {
  const [settings, setSettings] = useState<CookieSettings>({
    essential: true,
    analytics: false,
    marketing: false,
  });
  const [showSettings, toggleShowSettings] = useToggleState();
  const [isProcessing, setIsProcessing] = useState(false);
  const t = useTranslations('common.cookieConsent');

  if (!showConsent) return null;

  const handleAcceptAll = () => {
    setIsProcessing(true);
    setCookieConsent({
      essential: true,
      analytics: true,
      marketing: true,
      consentGiven: true,
    });
    setIsProcessing(false);
  };

  const handleRejectAll = () => {
    setIsProcessing(true);
    setCookieConsent({
      essential: true,
      analytics: false,
      marketing: false,
      consentGiven: true,
    });
    setIsProcessing(false);
  };

  const handleSave = () => {
    setIsProcessing(true);
    setCookieConsent({
      essential: settings.essential,
      analytics: settings.analytics,
      marketing: settings.marketing,
      consentGiven: true,
    });
    setIsProcessing(false);
  };

  const handleManageOrSave = () => {
    if (showSettings) {
      handleSave();
    } else {
      toggleShowSettings();
    }
  };

  const handleToggle = (settingKey: keyof CookieSettings) => (event: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = event.target.checked;
    const newSettings: CookieSettings = {
      ...settings,
      [settingKey]: newValue,
    };

    setSettings(newSettings);
    event.stopPropagation();
  };

  const cookieSettingsConfig: CookieSettingsConfigArray = [
    {
      key: 'essential' as const,
      name: t('necessary.title'),
      description: t('necessary.description'),
      cookies: [],
      required: true,
    },
    {
      key: 'analytics' as const,
      name: t('analytics.title'),
      description: t('analytics.description'),
      cookies: t.raw('analytics.cookies') || [],
      required: false,
    },
    {
      key: 'marketing' as const,
      name: t('marketing.title'),
      description: t('marketing.description'),
      cookies: t.raw('marketing.cookies') || [],
      required: false,
    },
  ];

  return (
    <Box className={cx(styles.container, { [styles.visible]: showConsent })}>
      <Stack direction="column" gap={4} className={styles.wrapper}>
        <Stack>
          <Typography component="p" variant="subtitle1" fontWeight={500} mb={3} color={colors.white}>
            {t('title')}
          </Typography>
          <Typography variant="body1" color={colors.white}>
            {t.rich('description', {
              link: renderCookiePolicyLink,
            })}
          </Typography>
        </Stack>
        <Collapse in={showSettings} timeout={300} unmountOnExit className={styles.wrapper}>
          <Stack direction="column" gap={2}>
            {cookieSettingsConfig.map(item => (
              <CookieAccordion
                title={item.name}
                checked={settings[item.key]}
                onChange={handleToggle(item.key)}
                disabled={item.required}
                key={item.key}
              >
                <CookieList description={item.description} cookies={item.cookies} />
              </CookieAccordion>
            ))}
          </Stack>
        </Collapse>
        <Stack direction={{ xs: 'column', md: 'row' }} gap={1} width="100%">
          <Button size="medium" onClick={handleAcceptAll} disabled={isProcessing} fullWidth>
            {t('buttons.accept')}
          </Button>
          <Button size="medium" variant="text" onClick={handleRejectAll} disabled={isProcessing} fullWidth>
            {t('buttons.reject')}
          </Button>
          <Button
            size="medium"
            variant="text"
            onClick={handleManageOrSave}
            disabled={isProcessing}
            endIcon={showSettings ? <Save size={20} /> : <Settings size={20} />}
            fullWidth
          >
            {showSettings ? t('buttons.save') : t('buttons.manage')}
          </Button>
        </Stack>
      </Stack>
    </Box>
  );
};

export default CookieConsent;
